import React, { useEffect, useMemo, useState } from "react";
import Icon from "../Icon";
import { MOCK_USER, MOCK_USERS, getMockDashboardForUser, matchesMockSearch } from "./mockData";

const STAT_CARDS = [
    { key: "open", label: "Open" },
    { key: "active", label: "Active" },
    { key: "inProgress", label: "In Progress" },
    { key: "overdue", label: "Overdue" },
    { key: "pending", label: "Pending" },
    { key: "total", label: "Total Tickets" },
    { key: "clarification", label: "Need Clarification" },
    { key: "confirmation", label: "Need Confirmation" },
    { key: "closed", label: "Closed" },
];

const PREVIEW_STAT_KEYS = ["open", "active", "pending", "total", "overdue", "closed"];

const MockDashboard = ({ departments = [], searchQuery = "", preview = false }) => {
    const [selectedUser, setSelectedUser] = useState(MOCK_USER.name);
    const [selectedDepts, setSelectedDepts] = useState([]);
    const [datePreset, setDatePreset] = useState("This Month");

    useEffect(() => {
        const validNames = new Set(departments.map((d) => d.name));
        setSelectedDepts((prev) => {
            const next = prev.filter((name) => validNames.has(name));
            return next.length === prev.length ? prev : next;
        });
    }, [departments]);

    const { stats, dashboard } = useMemo(
        () => getMockDashboardForUser(selectedUser),
        [selectedUser]
    );

    const visibleStats = useMemo(
        () => STAT_CARDS.filter((card) => matchesMockSearch(searchQuery, card.label, stats[card.key])),
        [searchQuery, stats]
    );
    const visibleAgents = useMemo(
        () => dashboard.topAgents.filter((a) => matchesMockSearch(searchQuery, a.name, a.resolved)),
        [searchQuery, dashboard.topAgents]
    );
    const visibleStatus = useMemo(
        () => dashboard.byStatus.filter((s) => matchesMockSearch(searchQuery, s.label, s.value)),
        [searchQuery, dashboard.byStatus]
    );
    const visiblePriority = useMemo(
        () => dashboard.byPriority.filter((p) => matchesMockSearch(searchQuery, p.label, p.value)),
        [searchQuery, dashboard.byPriority]
    );
    const visibleDepartments = useMemo(
        () => dashboard.byDepartment.filter((d) => matchesMockSearch(searchQuery, d.label, d.value)),
        [searchQuery, dashboard.byDepartment]
    );
    const visibleDeptFilters = useMemo(
        () => departments.filter((d) => matchesMockSearch(searchQuery, d.name)),
        [departments, searchQuery]
    );

    const statCards = useMemo(() => {
        if (preview) {
            return STAT_CARDS.filter((card) => PREVIEW_STAT_KEYS.includes(card.key));
        }
        return visibleStats.length ? visibleStats : STAT_CARDS;
    }, [preview, visibleStats]);

    const maxCreated = Math.max(...dashboard.ticketsCreated, 1);
    const maxStatus = Math.max(...(visibleStatus.length ? visibleStatus : dashboard.byStatus).map((s) => s.value), 1);
    const maxDept = Math.max(...(visibleDepartments.length ? visibleDepartments : dashboard.byDepartment).map((d) => d.value), 1);
    const maxAgent = Math.max(...(visibleAgents.length ? visibleAgents : dashboard.topAgents).map((a) => a.resolved), 1);
    const prioritySource = visiblePriority.length ? visiblePriority : dashboard.byPriority;
    const priorityTotal = prioritySource.reduce((sum, p) => sum + p.value, 0);

    const linePoints = dashboard.ticketsCreated.map((count, i) => {
        const x = (i / (dashboard.ticketsCreated.length - 1)) * 100;
        const y = 100 - (count / maxCreated) * 88;
        return `${x},${y}`;
    }).join(" ");

    const lineArea = `0,100 ${linePoints} 100,100`;

    let donutOffset = 0;
    const donutSegments = prioritySource.map((slice) => {
        const pct = (slice.value / priorityTotal) * 100;
        const start = donutOffset;
        donutOffset += pct;
        return { ...slice, start, pct };
    });

    const hasFilters = selectedUser !== MOCK_USER.name || selectedDepts.length > 0 || datePreset !== "This Month";

    const toggleDept = (name) => {
        setSelectedDepts((prev) =>
            prev.includes(name) ? prev.filter((d) => d !== name) : [...prev, name]
        );
    };

    const handleClearAll = () => {
        setSelectedUser(MOCK_USER.name);
        setSelectedDepts([]);
        setDatePreset("This Month");
    };

    return (
        <div className={`clk-mk-page clk-mk-page--dashboard${preview ? " clk-mk-page--dashboard-preview" : ""}`}>
            {!preview && (
            <div className="clk-mk-page-header-row">
                <h5>Dashboard</h5>
                <div className="clk-mk-dash-filters">
                    <div className="clk-mk-dash-filter-wrap">
                        <button type="button" className="clk-mk-dash-filter clk-mk-dash-filter--user">
                            <Icon name="user" size={14} />
                            {selectedUser}
                            <span className="clk-mk-filter-caret">▾</span>
                        </button>
                        <div className="clk-mk-dash-filter-menu">
                            {[MOCK_USER.name, ...MOCK_USERS.map((u) => u.name)]
                                .filter((name) => matchesMockSearch(searchQuery, name))
                                .map((name) => (
                                <button
                                    key={name}
                                    type="button"
                                    className={selectedUser === name ? "is-active" : ""}
                                    onClick={(e) => {
                                        setSelectedUser(name);
                                        e.currentTarget.blur();
                                    }}
                                >
                                    {name}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="clk-mk-dash-filter-wrap">
                        <button type="button" className="clk-mk-dash-filter">
                            Departments{selectedDepts.length ? ` (${selectedDepts.length})` : ""}
                            <span className="clk-mk-filter-caret">▾</span>
                        </button>
                        <div className="clk-mk-dash-filter-menu">
                            {visibleDeptFilters.map((d) => (
                                <button
                                    key={d.id}
                                    type="button"
                                    className={selectedDepts.includes(d.name) ? "is-active" : ""}
                                    onClick={(e) => {
                                        toggleDept(d.name);
                                        e.currentTarget.blur();
                                    }}
                                >
                                    {selectedDepts.includes(d.name) ? "✓ " : ""}{d.name}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="clk-mk-dash-filter-wrap">
                        <button type="button" className="clk-mk-dash-filter is-active">
                            {datePreset}
                            <span className="clk-mk-filter-caret">▾</span>
                        </button>
                        <div className="clk-mk-dash-filter-menu">
                            {["Today", "This Week", "This Month", "Last 30 Days"].map((preset) => (
                                <button
                                    key={preset}
                                    type="button"
                                    className={datePreset === preset ? "is-active" : ""}
                                    onClick={(e) => {
                                        setDatePreset(preset);
                                        e.currentTarget.blur();
                                    }}
                                >
                                    {preset}
                                </button>
                            ))}
                        </div>
                    </div>
                    {hasFilters && (
                        <button type="button" className="clk-mk-dash-clear" onClick={handleClearAll}>
                            Clear All
                        </button>
                    )}
                    <button
                        type="button"
                        className="clk-mk-outline-btn clk-mk-outline-btn--sm"
                        disabled
                        title="Not available in demo"
                    >
                        <Icon name="mail" size={13} />
                        Email Summary
                    </button>
                    <button
                        type="button"
                        className="clk-mk-outline-btn clk-mk-outline-btn--sm"
                        disabled
                        title="Not available in demo"
                    >
                        <Icon name="widget" size={13} />
                        Manage Dashboard
                    </button>
                </div>
            </div>
            )}

            {preview && <h5 className="clk-mk-dash-preview-title">Dashboard</h5>}

            <div className="clk-mk-dash-grid">
                <div className="clk-mk-dash-card clk-mk-dash-card--agents">
                    <div className="clk-mk-dash-card-head">
                        <strong>Top Agents</strong>
                        <span>Most resolved tickets, all time</span>
                    </div>
                    <div className="clk-mk-agent-bars">
                        {visibleAgents.map((agent) => (
                            <div key={agent.name} className="clk-mk-agent-row">
                                <span className="clk-mk-agent-name">{agent.name}</span>
                                <span className="clk-mk-agent-track">
                                    <i style={{ width: `${(agent.resolved / maxAgent) * 100}%` }} />
                                </span>
                                <span className="clk-mk-agent-val">{agent.resolved}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="clk-mk-dash-stats">
                    {statCards.map((card) => (
                        <div key={card.key} className="clk-mk-dash-stat">
                            <span className="clk-mk-dash-stat-label">{card.label}</span>
                            <strong>{stats[card.key]}</strong>
                        </div>
                    ))}
                </div>

                {!preview && (
                <>
                <div className="clk-mk-dash-card clk-mk-dash-card--line">
                    <div className="clk-mk-dash-card-head">
                        <strong>Tickets Created</strong>
                        <span>Daily volume of new tickets (last 30 days)</span>
                    </div>
                    <div className="clk-mk-line-chart">
                        <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                            <polygon className="clk-mk-line-area" points={lineArea} />
                            <polyline className="clk-mk-line-stroke" points={linePoints} />
                        </svg>
                    </div>
                </div>

                <div className="clk-mk-dash-card clk-mk-dash-card--status">
                    <div className="clk-mk-dash-card-head">
                        <strong>Tickets by Status</strong>
                    </div>
                    <div className="clk-mk-vbars">
                        {(visibleStatus.length ? visibleStatus : dashboard.byStatus).map((bar) => (
                            <div key={bar.label} className="clk-mk-vbar-col">
                                <span className="clk-mk-vbar-fill" style={{ height: `${(bar.value / maxStatus) * 100}%`, background: bar.color }} />
                                <span className="clk-mk-vbar-val">{bar.value}</span>
                                <span className="clk-mk-vbar-lbl">{bar.label}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="clk-mk-dash-card clk-mk-dash-card--priority">
                    <div className="clk-mk-dash-card-head">
                        <strong>Tickets by Priority</strong>
                    </div>
                    <div className="clk-mk-donut-wrap">
                        <div
                            className="clk-mk-donut"
                            style={{
                                background: `conic-gradient(${donutSegments.map((s) => `${s.color} ${s.start}% ${s.start + s.pct}%`).join(", ")})`,
                            }}
                        />
                        <ul className="clk-mk-donut-legend">
                            {(visiblePriority.length ? visiblePriority : dashboard.byPriority).map((p) => (
                                <li key={p.label}>
                                    <i style={{ background: p.color }} />
                                    {p.label}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="clk-mk-dash-card clk-mk-dash-card--dept">
                    <div className="clk-mk-dash-card-head">
                        <strong>Tickets per Department</strong>
                    </div>
                    <div className="clk-mk-dept-chart">
                        {(visibleDepartments.length ? visibleDepartments : dashboard.byDepartment).map((dept) => (
                            <div key={dept.label} className="clk-mk-dept-chart-row">
                                <span className="clk-mk-dept-chart-lbl">{dept.label}</span>
                                <span className="clk-mk-dept-chart-track">
                                    <i style={{ width: `${(dept.value / maxDept) * 100}%`, background: dept.color }} />
                                </span>
                                <span className="clk-mk-dept-chart-val">{dept.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
                </>
                )}
            </div>
        </div>
    );
};

export default MockDashboard;
